import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { RejectCandidateConfirmationPopup } from '~/components/PermanentJobsV2/JobOverview';
import { DeclineCandidateConfirmationPopup } from '~/components/PermanentJobsV2/JobOverview/components';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { readConfirmCandidateSource } from '~/domain/ConfirmCandidateSource';
import { useConfirmJobMutation, useDeclineCandidateMutation } from '~/domain/Jobs';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import {
  fireConfirmCandidateConfirmClicked,
  fireConfirmCandidateDeclineClicked,
  fireConfirmCandidateDeclineConfirmed,
} from '~/posthogs/events';
import { getCandidatePosition } from '../utils';
import { NO_CARD_ERROR, SHOW_MESSAGE } from '../../../actions';
import { Button } from '@tempmee/react';
import { isPartialShift } from '../../../commonComponents/CommonFunctions';
import CounterShiftPopup from '../../CounterShiftPopup';
import DeclineCandidatePopup from '../../DeclineCandidatePopup';

const getAdjacentCounterOffer = (allCandidates = [], counterOfferId) => {
  const declinedIndex = allCandidates.findIndex(
    (counterOffer) => counterOffer?.id === counterOfferId,
  );

  if (declinedIndex === -1) {
    return null;
  }

  return allCandidates[declinedIndex + 1] ?? allCandidates[declinedIndex - 1] ?? null;
};

const getMutationErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string') {
    return responseData;
  }

  return (
    responseData?.message ||
    responseData?.error ||
    responseData?.title ||
    error?.message ||
    'An unexpected error occurred.'
  );
};

export const ConfirmOrDecline = ({
  candidateId,
  job,
  currentCounter: providedCurrentCounter,
  allCandidates,
  viewedPosition,
  notesForTemp,
  allowNavigation,
  onDeclineConfirmed,
  onConfirmOutcome,
  onConfirmPendingChange,
  onDeclinePendingChange,
  onDeclineSuccess,
  onPaymentMissing,
  small = false,
}) => {
  const dispatch = useDispatch();
  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);
  const confirmJobMutation = useConfirmJobMutation({
    onOutcome: ({ outcome, response }, variables) => {
      if (outcome.type === 'show_another_job_popup') {
        onConfirmOutcome?.({ outcome, response }, variables);
        return;
      }

      if (outcome.type === 'show_eligible_jobs_popup') {
        onConfirmOutcome?.({ outcome, response }, variables);
        return;
      }

      if (outcome.type === 'onsite_confirmed') {
        const m2mOnsiteInterviewMessage =
          `Good news, ${variables.candidate?.full_name} has been confirmed ` +
          `for your onsite interview on ${outcome.localDate}.`;

        dispatch({
          type: SHOW_MESSAGE,
          payload: isEnablePermanentJobM2M
            ? {
                title: 'Interview Confirmed!',
                message: m2mOnsiteInterviewMessage,
                route: `/permanent-jobs/job-overview/${outcome.postingId}`,
                isSuccess: true,
              }
            : {
                title: 'Good News!',
                message: 'Your onsite interview has successfully been filled.',
                route: `/permanent-jobs/job-overview/${outcome.postingId}`,
                isSuccess: true,
              },
        });
        return;
      }

      if (outcome.type === 'confirmed_no_followup') {
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            title: 'Good News!',
            message: 'Your shift has successfully been filled.',
            route: outcome.route,
            isSuccess: true,
          },
        });
      }
    },
  });
  const declineCandidateMutation = useDeclineCandidateMutation();

  const [declineCandidatePopup, setDeclineCandidatePopup] = useState(false);
  const [counterShiftPopup, setCounterShiftPopup] = useState(false);
  const currentCounter =
    providedCurrentCounter ??
    allCandidates?.find((counter) => counter?.candidate?.id === candidateId);
  const isCounterHours = isPartialShift(currentCounter);

  const isOnsiteInterview = job?.isOnsiteInterview;

  // Shared by every confirm/decline event so each action carries the same
  // professional + offer + list-position context that candidate_viewed already
  // emits, making impressions joinable to outcomes without stitching across a
  // session (GOTU-4816 / GOTU-4788).
  //
  // Position is reused from the impression rather than recomputed here. Declining
  // a candidate invalidates the counter list, so the list this component sees at
  // click time can be shorter than the one the office was actually looking at —
  // recomputing reported the survivor of a 2-candidate shift as "1 of 1" when her
  // own candidate_viewed said "2 of 2". Sharing getCandidatePosition only
  // guaranteed both call sites derive position the same way; it could not stop the
  // input list changing between them.
  //
  // Falls back to a live derivation when no impression has been recorded for this
  // candidate (e.g. the single-candidate view, where the effect never ran) — there
  // is nothing to join to in that case, so a fresh position is the best available.
  const positionAtImpression =
    viewedPosition?.candidateId === candidateId
      ? viewedPosition.position
      : getCandidatePosition(allCandidates, candidateId);

  const eventContext = {
    shiftId: job?.id,
    professionalId: currentCounter?.candidate?.id ?? candidateId,
    offerId: currentCounter?.id,
    ...positionAtImpression,
  };

  const handleMutationError = (error) => {
    if (error?.response?.status === NO_CARD_ERROR) {
      onPaymentMissing?.();
      return;
    }

    dispatch({
      type: SHOW_MESSAGE,
      payload: {
        title: 'Sorry!',
        message: getMutationErrorMessage(error),
        isError: true,
        buttonText: 'Close',
      },
    });
  };

  const declineCandidatePressed = () => {
    fireConfirmCandidateDeclineClicked(eventContext);
    setDeclineCandidatePopup(true);
  };

  const handleAcceptClicked = () => {
    setCounterShiftPopup(false);
    confirmOffer();
  };

  const handleCancelClicked = () => {
    setCounterShiftPopup(false);
  };

  const handleConfirmDecline = (reason = '') => {
    fireConfirmCandidateDeclineConfirmed(eventContext);
    onDeclineConfirmed?.(allCandidates?.length > 1);
    const declinePayload = {
      jobId: job.id,
      counterOfferId: currentCounter?.id,
      cancellation_reason: reason,
      candidateId: currentCounter?.candidate?.id,
    };

    const nextCandidateId = !isOnsiteInterview
      ? getAdjacentCounterOffer(allCandidates, currentCounter?.id)?.candidate?.id
      : null;

    if (nextCandidateId) {
      declinePayload.nextCandidateId = nextCandidateId;
    }

    void (async () => {
      onDeclinePendingChange?.(true);
      try {
        const response = await declineCandidateMutation.mutateAsync(declinePayload);
        if (response?.isOnsiteInterview) {
          onDeclineSuccess?.();
        }
      } catch (error) {
        handleMutationError(error);
      } finally {
        onDeclinePendingChange?.(false);
      }
    })();
    setDeclineCandidatePopup(false);
  };

  const confirmOffer = () => {
    // `source` is read at click time rather than kept in `eventContext` because it
    // belongs to confirm_clicked alone (GOTU-4906) — the decline events share this
    // context and deliberately don't report it.
    fireConfirmCandidateConfirmClicked({
      ...eventContext,
      source: readConfirmCandidateSource(job?.id),
    });
    allowNavigation();

    void (async () => {
      onConfirmPendingChange?.(true);
      try {
        await confirmJobMutation.mutateAsync({
          jobId: job.id,
          data: { notes: notesForTemp },
          counterOfferId: currentCounter?.id,
          candidate: currentCounter?.candidate,
          officeId: job?.offer_owner?.id,
        });
      } catch (error) {
        handleMutationError(error);
      } finally {
        onConfirmPendingChange?.(false);
      }
    })();
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
      }}
    >
      <Button
        variant="outlined"
        size={small ? 'xs' : 'md'}
        style={{ marginRight: !small ? 20 : 12 }}
        onClick={declineCandidatePressed}
      >
        Decline
      </Button>
      <Button
        size={small ? 'xs' : 'md'}
        onClick={() => {
          if (isCounterHours) {
            setCounterShiftPopup(true);
          } else {
            confirmOffer();
          }
        }}
      >
        Confirm
      </Button>

      {declineCandidatePopup && (
        <>
          {isOnsiteInterview ? (
            <>
              {isEnablePermanentJobM2M ? (
                <DeclineCandidateConfirmationPopup
                  isLoading={declineCandidateMutation.isPending}
                  jobLocalDate={job.local_date}
                  professionalName={`${currentCounter?.candidate?.first_name} ${currentCounter?.candidate?.last_name}`}
                  onClose={() => setDeclineCandidatePopup(false)}
                  onConfirm={() => handleConfirmDecline('')}
                />
              ) : (
                <RejectCandidateConfirmationPopup
                  isLoading={declineCandidateMutation.isPending}
                  professionalName={`${currentCounter?.candidate?.first_name} ${currentCounter?.candidate?.last_name}`}
                  useDeclineTerms
                  onClose={() => setDeclineCandidatePopup(false)}
                  onConfirm={() => handleConfirmDecline('')}
                />
              )}
            </>
          ) : (
            <DeclineCandidatePopup
              declineBtnPressed={handleConfirmDecline}
              goBackPressed={() => setDeclineCandidatePopup(false)}
            />
          )}
        </>
      )}

      {counterShiftPopup ? (
        <CounterShiftPopup
          job={job}
          currentCounter={currentCounter}
          acceptBtnPressed={handleAcceptClicked}
          cancelBtnPressed={handleCancelClicked}
        />
      ) : null}
    </div>
  );
};
